/**
 * Admin API payload → domain model mapping.
 *
 * Keeps the nullable, PascalCase-ish API surface out of the admin UI: screens
 * receive fully populated models with empty strings instead of nulls.
 */
import { IS_DEV } from '../../config/env';
import { getFullImageUrl, resolveProfilePictureUrl } from '../../utils/imageUrl';
import type {
  AdminLoginEvent,
  AdminPage,
  AdminPagedResult,
  AdminUser,
  AdminUserDetailsResponse,
  AdminUserRole,
  ApiReportedContentComment,
  ApiReportedContentData,
  ApiReportedContentItem,
  DashboardMetric,
  DashboardNew,
  LoginInfoItem,
  ReportedContentItem,
  ReportedContentPage,
} from './adminTypes';

const text = (value: string | null | undefined): string => value ?? '';

const count = (value: number | null | undefined): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

/** Accepts the string-encoded integers some admin endpoints emit. */
const numeric = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const optionalText = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

/**
 * Property lookup keyed by lower-cased name. The admin controller is not
 * consistent about casing (`userGUID`, `reportedPostID`), so dashboard fields
 * are read case-insensitively rather than trusting one spelling.
 */
const indexByLowerKey = (source: Record<string, unknown>): Map<string, unknown> => {
  const index = new Map<string, unknown>();
  for (const [key, value] of Object.entries(source)) {
    index.set(key.toLowerCase(), value);
  }
  return index;
};

/**
 * Distinctive dashboard counter names. Any one identifies the body, so a
 * response omitting some counters still resolves.
 *
 * `metrics` is deliberately NOT a marker: it is an array the API could also
 * hang off an outer envelope, and matching on it alone would resolve to that
 * envelope and read every counter as 0.
 */
const DASHBOARD_MARKERS = [
  'allusers',
  'activeusers',
  'userloggedindaterange',
  'newusersindaterange',
  'totalpostsindaterange',
  'uniquepostsindaterange',
  'returnusersindaterange',
  'totaluploadphotosindaterange',
  'usersloggedintoday',
  'usersloggedinyesterday',
  'usersloggedinthismonth',
  'usersloggedinpast30days',
];

const carriesDashboardFields = (index: Map<string, unknown>): boolean =>
  DASHBOARD_MARKERS.some(marker => index.has(marker));

const MAX_UNWRAP_DEPTH = 5;

/**
 * Wrapper properties checked before any broader scan, most specific first.
 * GetDashboardNewMetrics actually answers `{ dashboard, status, statusCode }`
 * rather than the flat object the API document shows.
 */
const WRAPPER_KEYS = ['dashboard', 'data', 'result', 'payload'];

/**
 * Locates the dashboard body. The documented shape is flat, the live shape
 * nests it under `dashboard`, and other operations on this controller use a
 * `data` envelope, so the known wrappers are tried in order and anything else
 * is found by shape. Also tolerates a JSON-encoded body and a single-element
 * collection.
 */
const unwrapDashboard = (payload: unknown): Record<string, unknown> | null => {
  const visit = (
    value: unknown,
    depth: number,
  ): Record<string, unknown> | null => {
    if (value == null || depth > MAX_UNWRAP_DEPTH) {
      return null;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        return null;
      }
      try {
        return visit(JSON.parse(trimmed), depth + 1);
      } catch {
        return null;
      }
    }
    if (Array.isArray(value)) {
      for (const entry of value) {
        const found = visit(entry, depth + 1);
        if (found) {
          return found;
        }
      }
      return null;
    }
    if (typeof value !== 'object') {
      return null;
    }

    const record = value as Record<string, unknown>;
    const index = indexByLowerKey(record);
    if (carriesDashboardFields(index)) {
      return record;
    }
    for (const wrapper of WRAPPER_KEYS) {
      const found = visit(index.get(wrapper), depth + 1);
      if (found) {
        return found;
      }
    }
    for (const nested of Object.values(record)) {
      const found = visit(nested, depth + 1);
      if (found) {
        return found;
      }
    }
    return null;
  };

  return visit(payload, 0);
};

const mapDashboardMetric = (entry: unknown): DashboardMetric => {
  const index = indexByLowerKey(
    entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : {},
  );
  return {
    metrickey: optionalText(index.get('metrickey')),
    metricname: optionalText(index.get('metricname')),
    desktop: numeric(index.get('desktop')),
    android: numeric(index.get('android')),
    ios: numeric(index.get('ios')),
  };
};

/**
 * Normalises GetDashboardNewMetrics. Returns null when the payload carries no
 * recognisable dashboard fields so the service can reject rather than render a
 * screen of zeros.
 */
export const mapDashboard = (payload: unknown): DashboardNew | null => {
  const source = unwrapDashboard(payload);
  if (!source) {
    if (IS_DEV) {
      console.warn(
        '[admin] GetDashboardNewMetrics returned an unrecognised shape',
        payload,
      );
    }
    return null;
  }

  const index = indexByLowerKey(source);
  const read = (key: keyof DashboardNew): number =>
    numeric(index.get(key.toLowerCase()));

  const rawMetrics = index.get('metrics');
  const dashboard: DashboardNew = {
    userLoggedInDateRange: read('userLoggedInDateRange'),
    newUsersInDateRange: read('newUsersInDateRange'),
    totalPostsInDateRange: read('totalPostsInDateRange'),
    uniquePostsInDateRange: read('uniquePostsInDateRange'),
    returnUsersInDateRange: read('returnUsersInDateRange'),
    timeOnlineInDateRange: optionalText(index.get('timeonlineindaterange')),
    allUsers: read('allUsers'),
    activeUsers: read('activeUsers'),
    usersloggedIntoday: read('usersloggedIntoday'),
    usersloggedInYesterday: read('usersloggedInYesterday'),
    usersloggedInthisMonth: read('usersloggedInthisMonth'),
    usersLoggedinpast30days: read('usersLoggedinpast30days'),
    totalposts: read('totalposts'),
    uniqueposts: read('uniqueposts'),
    newusers: read('newusers'),
    returnusers: read('returnusers'),
    totalcomments: read('totalcomments'),
    totalshares: read('totalshares'),
    totalUploadPhotosInDateRange: read('totalUploadPhotosInDateRange'),
    metrics: Array.isArray(rawMetrics) ? rawMetrics.map(mapDashboardMetric) : [],
  };

  if (IS_DEV) {
    const mapped = new Set(Object.keys(dashboard).map(key => key.toLowerCase()));
    const unmapped = [...index.keys()].filter(key => !mapped.has(key));
    if (unmapped.length > 0) {
      console.warn(
        '[admin] GetDashboardNewMetrics returned unmapped fields',
        unmapped,
        payload,
      );
    }
  }

  return dashboard;
};

const mapLoginEvent = (item: LoginInfoItem): AdminLoginEvent => ({
  loginAt: text(item.loginTimestamp),
  loggedOutAt: text(item.loggedOutTimestamp),
  ipAddress: text(item.ipAddress),
  userAgent: text(item.userAgent),
  minutes: count(item.mins),
  result: text(item.result),
});

export const mapAdminUser = (item: AdminUserDetailsResponse): AdminUser => ({
  userId: count(item.userId),
  userGuid: text(item.userGUID),
  userName: text(item.userName),
  email: text(item.email),
  firstName: text(item.firstName),
  lastName: text(item.lastName),
  role: (item.role as AdminUserRole | undefined) ?? null,
  isEmailVerified: item.isEmailVerified === true,
  isUserBlocked: item.isUserBlocked === true,
  isActive: item.isActive === true,
  gender: text(item.gender),
  aboutMe: text(item.aboutMe),
  dateOfBirth: text(item.dob),
  // Distinguished from 0: an account with no date of birth has no age, and a
  // literal zero would read as a newborn.
  age: typeof item.age === 'number' && Number.isFinite(item.age) ? item.age : null,
  country: text(item.country),
  picture: text(item.picture),
  joiningDate: text(item.joiningDate),
  lastLoginOn: text(item.lastLoginOn),
  lastLoginIp: text(item.lastloginIp),
  updatedOn: text(item.updatedOn),
  firstPostDate: text(item.firstpostdate),
  lastPostDate: text(item.lastpostdate),
  totalPosts: count(item.totalpost),
  totalVisits: count(item.totalvisits),
  hasReadOnlyAccess: item.hasReadOnlyAccess === true,
  loginHistory: Array.isArray(item.loginInfo)
    ? item.loginInfo.map(mapLoginEvent)
    : [],
});

/**
 * True when a payload looks like a paged envelope, even when its page is
 * empty.
 *
 * Every collection on these endpoints is `nullable` in the API document, and an
 * unassigned .NET `List<T>` serialises as `null`, so the array being absent
 * cannot be treated as failure — that would turn "no rows match this filter"
 * into an error screen. Key presence is what separates a real empty page from
 * an unusable body.
 */
export const isPagedEnvelope = (
  payload: unknown,
  collectionKey: string,
): boolean => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  const index = indexByLowerKey(payload as Record<string, unknown>);
  return (
    index.has(collectionKey.toLowerCase()) ||
    index.has('totalcount') ||
    index.has('totalrecords')
  );
};

/**
 * Normalises any of the paged envelopes the admin endpoints return. Page
 * numbers fall back to the request's own values, because an empty last page
 * comes back with zeroed metadata on some endpoints.
 */
export const mapAdminPage = <TApi, TModel>(
  envelope: AdminPagedResult<TApi> | null | undefined,
  mapItem: (item: TApi) => TModel,
  requested: { pageNumber: number; pageSize: number },
): AdminPage<TModel> => {
  const items = Array.isArray(envelope?.items) ? envelope!.items! : [];
  const pageSize = count(envelope?.pageSize) || requested.pageSize;
  const totalCount = count(envelope?.totalCount);
  const totalPages =
    count(envelope?.totalPages) ||
    (pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0);
  const pageNumber = count(envelope?.pageNumber) || requested.pageNumber;

  return {
    items: items.map(mapItem),
    totalCount,
    pageNumber,
    pageSize,
    totalPages,
    hasNextPage: envelope?.hasNextPage ?? pageNumber < totalPages,
    hasPreviousPage: envelope?.hasPreviousPage ?? pageNumber > 1,
  };
};

const mediaList = (paths: readonly (string | null | undefined)[]): string[] =>
  paths
    .map(path => getFullImageUrl(path))
    .filter((url): url is string => typeof url === 'string');

const readReportedContentData = (
  record: Record<string, unknown>,
): ApiReportedContentData | null => {
  const index = indexByLowerKey(record);
  const posts = index.get('posts');
  const totalRecords = index.get('totalrecords');
  const comments = index.get('comments');

  // The rows themselves, not a count, are what makes a body usable: a payload
  // carrying `totalRecords: 5` and no `posts` would render an empty queue that
  // still believes five reports are waiting, and "Load more" would keep asking
  // for a page that never arrives.
  if (!Array.isArray(posts)) {
    return null;
  }
  return {
    totalRecords: typeof totalRecords === 'number' ? totalRecords : null,
    posts: posts as ApiReportedContentItem[],
    comments: Array.isArray(comments)
      ? (comments as ApiReportedContentComment[])
      : [],
  };
};

/**
 * Unwraps the GetReportedPosts body, which arrives as
 * `{ status, statusCode, data: { totalRecords, posts, comments } }`.
 *
 * Returns null — not an empty page — when nothing recognisable is present, so
 * the service can reject. An empty page here would be indistinguishable from
 * "no reports", which would show operators an empty moderation queue and leave
 * reported content unreviewed.
 */
export const extractReportedContentData = (
  payload: unknown,
): ApiReportedContentData | null => {
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const direct = readReportedContentData(record);
    if (direct) {
      return direct;
    }
    const nested = indexByLowerKey(record).get('data');
    if (nested && typeof nested === 'object') {
      const unwrapped = readReportedContentData(nested as Record<string, unknown>);
      if (unwrapped) {
        return unwrapped;
      }
    }
  }
  if (IS_DEV && payload != null) {
    console.warn(
      '[admin] GetReportedPosts returned an unrecognised shape',
      payload,
    );
  }
  return null;
};

export const mapReportedContentItem = (
  item: ApiReportedContentItem,
  commentsById?: ReadonlyMap<number, ApiReportedContentComment>,
): ReportedContentItem => {
  const commentId = count(item.reportedCommentId);
  const isComment = commentId > 0;
  // A reported comment's body lives in the sibling `comments` collection; it is
  // absent when the comment was deleted or falls outside the rows the endpoint
  // returns. The post's own body must not stand in for it — that would put the
  // wrong text, author and media in front of the decision — so the card shows
  // its "no content" state instead.
  const comment = isComment ? commentsById?.get(commentId) : undefined;
  const postTitle = text(item.title);

  return {
    reportedPostId: count(item.reportedPostId),
    postId: count(item.shareId),
    commentId,
    isComment,
    content: isComment ? text(comment?.commentMessage) : text(item.postDescription),
    title: isComment ? '' : postTitle,
    parentPostTitle: isComment ? postTitle : '',
    postImages: isComment
      ? mediaList([comment?.images])
      : mediaList(item.photoAlbum ?? []),
    reason: text(item.reportedComments),
    reportedBy: text(item.reporter?.userName),
    reportedByAvatar: resolveProfilePictureUrl(item.reporter?.userPhoto) ?? '',
    reportedByEmail: '',
    postedBy: text(isComment ? comment?.userName : item.author?.userName),
    postedByAvatar:
      resolveProfilePictureUrl(
        isComment ? comment?.userPhoto : item.author?.userPhoto,
      ) ?? '',
    postedByEmail: '',
    postedOn: text(
      isComment
        ? comment?.createdDateTime
        : item.postDate ?? item.createdDateTime,
    ),
    reportedOn: text(item.reportedTime ?? item.reporter?.reportedTime),
    groupName: '',
    isBlog: text(item.type).toLowerCase() === 'blog',
    likes: count(isComment ? comment?.likeCount : item.likeCount),
    comments: isComment ? 0 : count(item.commentCount),
    shares: 0,
    isReviewed: item.isReportReviewed === true,
    isApproved: item.isReportApproved === true,
    isPostActive: item.isPostActive !== false,
    reviewerComments: text(item.reviewerComments),
  };
};

export const mapReportedContent = (
  data: ApiReportedContentData,
): ReportedContentPage => {
  const commentsById = new Map<number, ApiReportedContentComment>();
  for (const comment of data.comments ?? []) {
    const id = count(comment.commentId);
    if (id > 0 && !commentsById.has(id)) {
      commentsById.set(id, comment);
    }
  }
  return {
    items: (data.posts ?? []).map(post =>
      mapReportedContentItem(post, commentsById),
    ),
    totalRecords: count(data.totalRecords),
  };
};
